import React from 'react';
import { Link } from 'react-router-dom';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';

export interface PrimaryNavigationItem {
  title: string;
  description: string;
  link: string;
}

export interface SecondaryNavigationItem {
  title: string;
  links: {
    text: string;
    href: string;
  }[];
}

export interface NavigationDropdownContentProps {
  primaryItems?: PrimaryNavigationItem[];
  secondaryItems?: SecondaryNavigationItem[];
  isOpen: boolean;
  onItemClick: () => void;
  primaryGridColumns?: number;
  secondaryGridTemplate?: string;
}

export const NavigationDropdownContent = ({
  primaryItems = [],
  secondaryItems = [],
  isOpen,
  onItemClick,
  primaryGridColumns = 4,
  secondaryGridTemplate = '400px 184px 184px',
}: NavigationDropdownContentProps): React.ReactElement => (
  <>
    {primaryItems.length > 0 && (
      <ul
        css={[
          defaultListStyles,
          css({
            padding: `${token('space.500')} !important`,
            gap: token('space.400'),
            backgroundColor: '#FFFFFF',
            display: 'grid',
            gridTemplateColumns: `repeat(${primaryGridColumns}, 184px)`,
          }),
        ]}
      >
        {primaryItems.map(({ title, description, link }) => (
          <li key={title} css={[defaultListItemStyles, primaryItemStyles]}>
            <Link tabIndex={isOpen ? 0 : -1} css={primaryItemLinkStyles} to={link} onClick={onItemClick}>
              <span css={primaryItemTitleStyles}>{title}</span>
              <span css={primaryItemDescriptionStyles}>{description}</span>
            </Link>
          </li>
        ))}
      </ul>
    )}
    {secondaryItems.length > 0 && (
      <ul
        css={[
          defaultListStyles,
          css({
            display: 'grid',
            gridTemplateColumns: secondaryGridTemplate,
            textAlign: 'left',
            padding: `${token('space.500')} !important`,
            gap: token('space.400'),
            color: '#101214',
            backgroundColor: '#F8F8F8',
            marginTop: '0 !important',
          }),
        ]}
      >
        {secondaryItems.map(({ title, links }) => (
          <li key={title} css={defaultListItemStyles}>
            <span css={secondaryItemTitleStyles}>{title}</span>
            <ul css={[defaultListStyles, secondaryItemListStyles]}>
              {links.map(({ text, href }) => (
                <li key={text} css={[defaultListItemStyles, secondaryItemLinkWrapperStyles]}>
                  <Link tabIndex={isOpen ? 0 : -1} css={secondaryItemLinkStyles} to={href} onClick={onItemClick}>
                    {text}
                  </Link>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    )}
  </>
);

const defaultListStyles = css({
  display: 'flex',
  padding: 0,
  margin: 0,
});

const defaultListItemStyles = css({
  listStyle: 'none',
  padding: 0,
  margin: 0,
  maxWidth: '184px',
  width: '100%',
});

const primaryItemStyles = css({
  '&:hover span:first-of-type': {
    color: '#0052CC',
  },
});

const primaryItemLinkStyles = css({
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'left',
  textDecoration: 'none !important',
});

const primaryItemTitleStyles = css({
  fontFamily: 'Charlie Text, sans-serif',
  fontSize: '16px',
  fontWeight: 700,
  lineHeight: '24px',
  letterSpacing: 0,
  color: '#101214',
  marginBottom: token('space.050'),
});

const primaryItemDescriptionStyles = css({
  fontFamily: 'Charlie Text, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20px',
  letterSpacing: '0',
  color: token('color.text'),
});

const secondaryItemTitleStyles = css({
  fontFamily: 'Charlie Text, sans-serif',
  fontSize: '12px',
  fontWeight: 700,
  lineHeight: '16px',
  letterSpacing: 0,
  color: '#101214',
  marginBottom: token('space.150'),
  textTransform: 'uppercase',
  display: 'inline-block',
});

const secondaryItemListStyles = css({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  flexWrap: 'wrap',
  rowGap: token('space.200'),
  columnGap: token('space.400'),
  maxHeight: '150px',
});

const secondaryItemLinkWrapperStyles = css({
  height: '24px',
  display: 'flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
});

const secondaryItemLinkStyles = css({
  textDecoration: 'none !important',
  color: token('color.text'),
  fontFamily: 'Charlie Text, sans-serif',
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '20px',
  letterSpacing: 0,
});
